import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { api } from "@/services/apiClient";
import type { ApiRequestOptions } from "@/services/iamApi";

const PAGINATION_MARKER_HEADER = "x-pagination-marker";

interface FetchPaginatedResourceParams<TItem> {
  pageLimit: number;
  requestOptions?: ApiRequestOptions;
  resourceUri: string;
  normalizePage: (data: unknown) => TItem[];
  sortItems: (items: readonly TItem[]) => TItem[];
  query?: Record<string, string | number | readonly string[] | undefined>;
  apiClient?: AxiosInstance;
}

export async function fetchPaginatedResource<TItem>({
  pageLimit,
  requestOptions = {},
  resourceUri,
  normalizePage,
  query = {},
  sortItems,
  apiClient = api,
}: FetchPaginatedResourceParams<TItem>): Promise<TItem[]> {
  const items: TItem[] = [];
  let pageMarker: string | undefined;

  do {
    const requestConfig: AxiosRequestConfig = {
      params: {
        page_limit: pageLimit,
        page_marker: pageMarker,
        ...query,
      },
      // A list is sent as the name repeated -- `fields=a&fields=b` -- which is
      // how the platform reads it, rather than as `fields[]=a`.
      paramsSerializer: { indexes: null },
    };
    if (requestOptions.signal) requestConfig.signal = requestOptions.signal;

    const { data, headers } = await apiClient.get<unknown>(
      resourceUri,
      requestConfig,
    );

    items.push(...normalizePage(data));
    pageMarker = readPaginationMarker(headers);
  } while (pageMarker);

  return sortItems(items);
}

export function extractList(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;

  if (!isRecord(data)) return [];

  for (const key of ["items", "results", "data"]) {
    const value = data[key];
    if (Array.isArray(value)) return value;
  }

  return [];
}

export function readPaginationMarker(
  headers: AxiosResponse<unknown>["headers"],
): string | undefined {
  const value = headers[PAGINATION_MARKER_HEADER];

  if (Array.isArray(value)) return value[0] || undefined;
  if (typeof value === "string" && value) return value;

  return undefined;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

export function readNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !value.trim()) return 0;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}
